import { useUser } from "@/application/providers/user";
import { usePagination } from "@/shared/navigation/pagination";
import { useQuery } from "@apollo/client";
import {
  GetTasksNotificationListQueryVariables,
  PageRequest,
  TaskSortType,
} from "gql/graphql";
import { GET_TASKS_NOTIFICATION_LIST } from "@/modules/common/widgets/notification-bell/api/get-tasks-notification-list";
import { useRef } from "react";

export function useTasksNotification() {
  const user = useUser();
  const { pageRequest } = usePagination();
  const nextPage = useRef(pageRequest.page + 1);

  const getVariables = (
    pageRequest: PageRequest,
  ): GetTasksNotificationListQueryVariables => ({
    filter: {
      person: user?.person?.id,
      isDone: false,
    },
    page: pageRequest,
    sort: { asc: true, type: TaskSortType.CreatedDate },
  });

  const { data, loading, fetchMore } = useQuery(GET_TASKS_NOTIFICATION_LIST, {
    variables: getVariables(pageRequest),
  });

  const loadMore = async () => {
    await fetchMore({
      variables: getVariables({
        ...pageRequest,
        page: nextPage.current,
      }),
      updateQuery: (previousResult, { fetchMoreResult }) => {
        nextPage.current = nextPage.current + 1;

        const loadedTasks = [
          ...previousResult.getTaskList.content,
          ...fetchMoreResult.getTaskList.content,
        ];

        const idsInResult: Record<string, boolean> = {};

        const uniqueTasks = loadedTasks.filter((task) => {
          if (idsInResult[task.id]) {
            return false;
          }

          idsInResult[task.id] = true;
          return true;
        });

        return {
          ...previousResult,
          getTaskList: {
            ...fetchMoreResult.getTaskList,
            content: uniqueTasks,
          },
        };
      },
    });
  };

  const tasks = data?.getTaskList.content ?? [];

  return {
    tasks,
    loading,
    loadMore,
    total: data?.getTaskList.total ?? 0,
  };
}
